/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
import {
    LineChart, Line, BarChart, Bar, AreaChart, Area, ResponsiveContainer,
    XAxis, YAxis, Tooltip, Legend, PieChart, Pie, Cell, RadialBarChart, RadialBar,
} from 'recharts';
import {
    Activity, AlertTriangle, ArrowRight, ArrowUpRight, BarChart3, Boxes, Brain,
    Building2, ChevronRight, Circle, CircuitBoard, CloudLightning, Compass,
    Database, DollarSign, Download, Eye, Factory, Fingerprint, FileCheck2, Gauge,
    Globe, GraduationCap, Layers, LineChart as LineIcon, Lock, MessageSquare,
    Network, PackageSearch, Radar, Radio, Receipt, RefreshCw, Rocket, Search,
    Server, ShieldCheck, ShoppingBag, Sparkles, Store, Target, TrendingUp, Users,
    Workflow, Wand2, Zap,
} from 'lucide-react';
import './FundleProposal.css';

/* ------------------------------------------------------------------ */
/* NAVIGATION                                                          */
/* ------------------------------------------------------------------ */
const NAV_SECTIONS = [
    { id: 'summary',       n: '01', label: 'Executive Summary' },
    { id: 'challenges',    n: '02', label: 'Industry Challenges' },
    { id: 'why-fundle',    n: '03', label: 'Why Fundle' },
    { id: 'platform',      n: '04', label: 'Platform Overview' },
    { id: 'differentiators', n: '05', label: 'Differentiators' },
    { id: 'connectivity',  n: '06', label: 'Brand Integration' },
    { id: 'architecture',  n: '07', label: 'Data Architecture' },
    { id: 'security',      n: '08', label: 'Security' },
    { id: 'accuracy',      n: '09', label: 'Data Accuracy & Audit' },
    { id: 'analytics',     n: '10', label: 'Analytics Platform' },
    { id: 'questions',     n: '11', label: 'Management Questions' },
    { id: 'mallgpt',       n: '12', label: 'AI Intelligence Layer' },
    { id: 'forecasting',   n: '13', label: 'Forecasting' },
    { id: 'customer',      n: '14', label: 'Customer Intelligence' },
    { id: 'tenant',        n: '15', label: 'Tenant Intelligence' },
    { id: 'operations',    n: '16', label: 'Operations' },
    { id: 'roadmap',       n: '17', label: 'Roadmap' },
    { id: 'implementation', n: '18', label: 'Implementation' },
    { id: 'reference',     n: '19', label: 'Reference Deployments' },
    { id: 'commercials',   n: '20', label: 'Why Fundle Wins' },
    { id: 'future',        n: '21', label: 'The Future' },
];

/* ------------------------------------------------------------------ */
/* HELPERS                                                              */
/* ------------------------------------------------------------------ */
function Counter({ to, suffix = '', prefix = '', decimals = 0, duration = 1.4 }) {
    const nodeRef = useRef(null);
    const inView = useInView(nodeRef, { once: true, amount: 0.5 });
    const val = useMotionValue(0);
    const [display, setDisplay] = useState(prefix + '0' + suffix);
    useEffect(() => {
        if (!inView) return;
        const controls = animate(val, to, { duration, ease: [0.16, 1, 0.3, 1] });
        const unsub = val.on('change', (v) => {
            setDisplay(prefix + v.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',') + suffix);
        });
        return () => { controls.stop(); unsub(); };
    }, [inView, to, prefix, suffix, decimals, duration, val]);
    return <span ref={nodeRef}>{display}</span>;
}

function Reveal({ children, delay = 0, y = 20 }) {
    return (
        <motion.div
            initial={{ opacity: 0, y }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1], delay }}
        >
            {children}
        </motion.div>
    );
}

function Section({ id, eyebrow, title, lead, children }) {
    return (
        <section id={id} className="fp-section" data-testid={`section-${id}`}>
            <Reveal>
                {eyebrow && <div className="fp-eyebrow">{eyebrow}</div>}
                {title && <h2 className="fp-title">{title}</h2>}
                {lead && <p className="fp-lead">{lead}</p>}
            </Reveal>
            {children}
        </section>
    );
}

/* ------------------------------------------------------------------ */
/* MAIN                                                                 */
/* ------------------------------------------------------------------ */
export default function FundleProposal() {
    const [active, setActive] = useState('summary');
    const sectionRefs = useRef({});

    useEffect(() => {
        document.title = 'Fundle Retail Intelligence Platform · Proposal for Inorbit Malls';
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) setActive(e.target.id);
                });
            },
            { rootMargin: '-30% 0px -60% 0px', threshold: 0 }
        );
        NAV_SECTIONS.forEach((s) => {
            const el = document.getElementById(s.id);
            if (el) { sectionRefs.current[s.id] = el; io.observe(el); }
        });
        return () => io.disconnect();
    }, []);

    const goTo = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <div className="fundle-proposal" data-testid="fundle-proposal">
            <div className="fp-shell">
                {/* LEFT NAV */}
                <aside className="fp-nav" data-testid="proposal-nav">
                    <div className="fp-brand">
                        <img src="/brand/fundle-logo.png" alt="Fundle" style={{ height: 34, width: 'auto', display: 'block' }} data-testid="brand-logo" />
                        <div className="fp-brand-sub" style={{ marginTop: 10 }}>Retail Intelligence Platform</div>
                    </div>

                    <div className="fp-nav-title">Proposal for Inorbit Malls</div>
                    <ul className="fp-nav-list">
                        {NAV_SECTIONS.map((s) => (
                            <li key={s.id}>
                                <a
                                    href={`#${s.id}`}
                                    className={`fp-nav-item ${active === s.id ? 'active' : ''}`}
                                    onClick={(e) => { e.preventDefault(); goTo(s.id); }}
                                    data-testid={`nav-${s.id}`}
                                >
                                    <span className="fp-nav-num">{s.n}</span>
                                    <span>{s.label}</span>
                                </a>
                            </li>
                        ))}
                    </ul>

                    <div className="fp-nav-footer">
                        <div>Confidential · February 2026</div>
                        <a href="/fundle-proposal.pdf" target="_blank" rel="noopener noreferrer" className="fp-nav-cta" data-testid="download-pdf-btn">
                            <Download size={12} /> Download PDF
                        </a>
                    </div>
                </aside>

                {/* MAIN */}
                <main className="fp-main">
                    <HeroSlide />
                    <ExecutiveSummary />
                    <Challenges />
                    <WhyFundle />
                    <PlatformOverview />
                    <Differentiators />
                    <Connectivity />
                    <Architecture />
                    <Security />
                    <Accuracy />
                    <Analytics />
                    <Questions />
                    <MallGPT />
                    <Forecasting />
                    <CustomerIntelligence />
                    <TenantIntelligence />
                    <OperationsIntelligence />
                    <Roadmap />
                    <Implementation />
                    <ReferenceDeployments />
                    <Commercials />
                    <TheFuture />
                </main>
            </div>
        </div>
    );
}

/* ================================================================== */
/* HERO                                                                 */
/* ================================================================== */
function HeroSlide() {
    return (
        <section id="hero" className="fp-hero" data-testid="hero-slide">
            <div className="fp-hero-orb" />
            <div className="fp-hero-orb-2" />
            <Reveal>
                <div className="fp-hero-tag" data-testid="hero-tag">
                    <span className="dot" /> Confidential Proposal · February 2026 · Prepared for Inorbit Malls
                </div>
                <h1 className="fp-hero-title">
                    Fundle Retail<br />Intelligence <em>Platform.</em>
                    <span className="subline">Powered by ADSR.</span>
                </h1>
                <p className="fp-hero-sub">
                    Beyond sales reporting. Building the intelligent mall — a single platform that
                    turns fragmented retail data into forecasts, benchmarks, anomalies and executive decisions.
                </p>
                <div className="fp-hero-ctas">
                    <a href="#summary" className="fp-btn-primary" data-testid="hero-cta-explore">
                        Explore the platform <ArrowRight size={16} />
                    </a>
                    <a href="/fundle-proposal.pdf" target="_blank" rel="noopener noreferrer" className="fp-btn-ghost">
                        <Download size={14} /> Download PDF
                    </a>
                </div>
            </Reveal>

            {/* Metric strip */}
            <div style={{ marginTop: 72 }}>
                <div className="fp-grid-4">
                    {[
                        { k: '95', s: '%+', l: 'Brand Integration Readiness', t: 'API · Agent · SFTP · CSV · Utility' },
                        { k: '99.9', s: '%', l: 'Platform Availability', t: 'Enterprise SLA · Redundant infra' },
                        { k: '15', s: 'min', l: 'Data Synchronisation', t: 'Near real-time reconciliation' },
                        { k: '100', s: '%', l: 'Audit Coverage', t: 'Every bill · Every store · Every day' },
                    ].map((m, i) => (
                        <Reveal key={i} delay={0.1 + i * 0.08}>
                            <div className="fp-kpi" data-testid={`hero-kpi-${i}`}>
                                <div className="fp-kpi-num">
                                    <Counter to={parseFloat(m.k)} decimals={m.k.includes('.') ? 1 : 0} />
                                    <span className="unit">{m.s}</span>
                                </div>
                                <div className="fp-kpi-label">{m.l}</div>
                                <div className="fp-kpi-sub">{m.t}</div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ================================================================== */
/* 01 EXECUTIVE SUMMARY                                                 */
/* ================================================================== */
function ExecutiveSummary() {
    return (
        <Section
            id="summary"
            eyebrow="01 · Executive Summary"
            title={<>Traditional malls collect data.<br /><em>Future malls create intelligence.</em></>}
            lead="Fundle transforms fragmented retail data into actionable business intelligence. Instead of receiving sales reports every day, management receives insights, warnings, predictions, recommendations, benchmarking and AI-assisted decisions."
        >
            <Reveal delay={0.1}>
                <div className="fp-grid-2" style={{ marginTop: 24 }}>
                    <div className="fp-card fp-card-dark">
                        <h4 className="fp-h4" style={{ color: 'rgba(255,255,255,0.6)' }}>THE OLD WORLD</h4>
                        <h3 className="fp-h3" style={{ marginTop: 8, color: 'white' }}>Reports arrive.<br />Decisions wait.</h3>
                        <ul className="fp-bullets" style={{ marginTop: 20 }}>
                            {['Manual daily sales collation via email + Excel',
                              'Late data, missing stores, no reconciliation',
                              'One-page PDFs with no context, no forecasts',
                              'Executive intuition instead of evidence',
                              'Retrospective — never proactive'].map((t, i) => (
                                <li key={i} style={{ color: 'rgba(255,255,255,0.85)', borderColor: 'rgba(255,255,255,0.15)' }}>{t}</li>
                              ))}
                        </ul>
                    </div>
                    <div className="fp-card" style={{ background: 'var(--fp-blue-soft)', border: '1px solid transparent' }}>
                        <h4 className="fp-h4">THE FUNDLE WORLD</h4>
                        <h3 className="fp-h3" style={{ marginTop: 8 }}>Intelligence arrives.<br />Decisions accelerate.</h3>
                        <ul className="fp-bullets check" style={{ marginTop: 20 }}>
                            {['Automatic collection from every POS / ERP / Cloud API',
                              'Reconciliation, anomaly detection, exception workflows',
                              'Executive dashboards + narrative insights + Mall GPT',
                              'Forecasts for tomorrow, weekend, month-end, festival',
                              'Proactive — surfaces the questions leadership hasn\'t asked'].map((t, i) => (
                                <li key={i} style={{ borderColor: 'rgba(30, 79, 219, 0.15)' }}>{t}</li>
                              ))}
                        </ul>
                    </div>
                </div>
            </Reveal>

            <Reveal delay={0.25}>
                <div style={{ marginTop: 48, padding: 32, background: 'var(--fp-canvas)', borderRadius: 'var(--fp-radius-lg)' }}>
                    <div className="fp-grid-5">
                        {[
                            { n: 95, s: '%+', l: 'Integration Readiness' },
                            { n: 99.9, s: '%', l: 'Platform Availability' },
                            { n: 15, s: 'min', l: 'Data Sync' },
                            { n: 100, s: '%', l: 'Audit Coverage' },
                            { n: 300, s: '+', l: 'Malls Scalable' },
                        ].map((m, i) => (
                            <div key={i} style={{ textAlign: 'center' }} data-testid={`summary-metric-${i}`}>
                                <div className="fp-kpi-num" style={{ fontSize: 44 }}>
                                    <Counter to={m.n} decimals={m.n.toString().includes('.') ? 1 : 0} />
                                    <span className="unit">{m.s}</span>
                                </div>
                                <div className="fp-kpi-label" style={{ marginTop: 8 }}>{m.l}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </Reveal>
        </Section>
    );
}

/* ================================================================== */
/* 02 INDUSTRY CHALLENGES                                               */
/* ================================================================== */
function Challenges() {
    const items = [
        { icon: Boxes, t: 'Fragmented POS Landscape', d: 'Every brand runs a different POS — Retail Pro, SAP, Oracle, Dynamics, LS, Shopify, Ginesys, GoFrugal, custom systems.' },
        { icon: RefreshCw, t: 'Manual Daily Collation', d: 'Ops teams chase emails, Excel sheets, WhatsApp forwards. Numbers arrive at 3 PM for yesterday.' },
        { icon: AlertTriangle, t: 'No Anomaly Detection', d: 'Missing bills, duplicate invoices, negative sales — spotted weeks later during monthly audit.' },
        { icon: Eye, t: 'Zero Hourly Visibility', d: 'Peak hour, dead hour, weekend patterns hidden. Staffing and operations run on gut feel.' },
        { icon: LineIcon, t: 'No Forecasting', d: 'No predictive engine for weekend, festival, weather, IPL, salary week or school holidays.' },
        { icon: Radar, t: 'No Benchmarking', d: 'Brand vs brand, mall vs mall, category vs category — comparisons are impossible without a normalised layer.' },
        { icon: Users, t: 'No Customer Understanding', d: 'Footfall counts exist. Repeat rate, catchment, LTV, cross-shopping — do not.' },
        { icon: Brain, t: 'No Predictive Intelligence', d: 'Which tenants deserve expansion? Which are at rental risk? Nobody knows until the lease expires.' },
        { icon: BarChart3, t: 'No Executive Dashboards', d: 'CXOs receive PDF reports. Not living, breathing decision surfaces.' },
    ];
    return (
        <Section
            id="challenges"
            eyebrow="02 · Industry Challenges"
            title={<>The mall economy runs on <em>fragmented signals.</em></>}
            lead="Modern malls collect enormous amounts of data — from brands, footfall counters, restaurants, entertainment, parking and events. Almost none of it becomes intelligence. Here is why."
        >
            <div className="fp-grid-3" style={{ marginTop: 32 }}>
                {items.map((it, i) => (
                    <Reveal key={i} delay={i * 0.05}>
                        <div className="fp-card" data-testid={`challenge-${i}`}>
                            <div className="fp-feature-icon" style={{ width: 40, height: 40, marginBottom: 12 }}>
                                <it.icon size={20} strokeWidth={1.8} />
                            </div>
                            <h3 className="fp-h3" style={{ fontSize: 17, marginBottom: 6 }}>{it.t}</h3>
                            <p className="fp-p" style={{ fontSize: 13, marginBottom: 0 }}>{it.d}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </Section>
    );
}

/* ================================================================== */
/* 03 WHY FUNDLE                                                        */
/* ================================================================== */
function WhyFundle() {
    const flow = [
        { l: 'Data', d: 'Multi-format ingestion from every POS / ERP / Cloud API', i: Database },
        { l: 'Validation', d: 'Schema, tax, item-line and store-hour rules', i: FileCheck2 },
        { l: 'Reconciliation', d: 'Daily match against expected sales & bill counts', i: Workflow },
        { l: 'AI', d: 'Forecasting, anomaly, benchmarking, natural-language layer', i: Brain },
        { l: 'Insights', d: 'Executive narratives, alerts, opportunities, warnings', i: Sparkles },
        { l: 'Decisions', d: 'Faster leasing, staffing, marketing, tenant management', i: Target },
    ];
    return (
        <Section
            id="why-fundle"
            eyebrow="03 · Why Fundle"
            title={<>Not sales reporting.<br /><em>A retail intelligence platform.</em></>}
            lead="Fundle is designed as an intelligence platform first. ADSR is the entry surface — the real value is the reconciliation engine, the AI layer, and the executive narrative that emerges above it."
        >
            <div style={{ marginTop: 40 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12, alignItems: 'stretch' }} data-testid="value-flow">
                    {flow.map((f, i) => (
                        <Reveal key={i} delay={i * 0.08}>
                            <div style={{
                                padding: '24px 16px', border: '1px solid var(--fp-hair)', borderRadius: 14,
                                background: i === flow.length - 1 ? 'var(--fp-navy)' : 'var(--fp-bg)',
                                color: i === flow.length - 1 ? 'white' : 'inherit',
                                position: 'relative', height: '100%',
                            }}>
                                <f.i size={22} strokeWidth={1.6} style={{ color: i === flow.length - 1 ? 'var(--fp-accent)' : 'var(--fp-blue)' }} />
                                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, marginTop: 14, fontWeight: 500 }}>{f.l}</div>
                                <div style={{ fontSize: 11.5, color: i === flow.length - 1 ? 'rgba(255,255,255,0.7)' : 'var(--fp-mute)', marginTop: 6, lineHeight: 1.4 }}>{f.d}</div>
                                {i < flow.length - 1 && (
                                    <div style={{
                                        position: 'absolute', right: -12, top: '50%', transform: 'translateY(-50%)',
                                        color: 'var(--fp-mute)', zIndex: 2,
                                    }}>
                                        <ChevronRight size={16} />
                                    </div>
                                )}
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>

            <Reveal delay={0.3}>
                <div style={{ marginTop: 48 }}>
                    <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 28, lineHeight: 1.4, color: 'var(--fp-navy)', fontStyle: 'italic', borderLeft: '3px solid var(--fp-blue)', paddingLeft: 24, maxWidth: 820 }}>
                        “A well-designed data platform doesn't just answer questions — it surfaces the questions leadership hasn't asked yet.”
                    </p>
                </div>
            </Reveal>
        </Section>
    );
}

/* ================================================================== */
/* 04 PLATFORM OVERVIEW                                                 */
/* ================================================================== */
function PlatformOverview() {
    const pillars = [
        { i: Network, t: 'Connectivity', d: 'API · Agent · Utility · SFTP · CSV · Custom middleware. The largest brand integration layer in Indian retail.' },
        { i: Brain, t: 'Intelligence', d: 'Forecasting, benchmarking, anomaly detection, natural-language Mall GPT, tenant health scoring.' },
        { i: ShieldCheck, t: 'Accuracy', d: 'Daily reconciliation, missing-sale alerts, duplicate detection, monthly audit, confidence scoring.' },
        { i: Layers, t: 'Extensibility', d: 'Retail media, loyalty, customer intelligence, digital twin — every future module runs on the same core.' },
    ];
    return (
        <Section
            id="platform"
            eyebrow="04 · Platform Overview"
            title={<>Four pillars. <em>One platform.</em></>}
            lead="Fundle is built as a layered platform. ADSR is the ingestion surface. Everything above — dashboards, AI, Mall GPT, forecasting, tenant scoring — composes on the same data spine."
        >
            <div className="fp-grid-4" style={{ marginTop: 32 }}>
                {pillars.map((p, i) => (
                    <Reveal key={i} delay={i * 0.08}>
                        <div className="fp-feature-card" data-testid={`pillar-${i}`}>
                            <div className="fp-feature-num">0{i + 1}</div>
                            <div className="fp-feature-icon"><p.i size={22} strokeWidth={1.6} /></div>
                            <h3 className="fp-h3" style={{ fontSize: 20 }}>{p.t}</h3>
                            <p className="fp-p" style={{ fontSize: 13.5 }}>{p.d}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </Section>
    );
}

/* ================================================================== */
/* 05 DIFFERENTIATORS                                                   */
/* ================================================================== */
function Differentiators() {
    return (
        <Section
            id="differentiators"
            eyebrow="05 · Biggest Differentiators"
            title={<>What makes Fundle <em>the platform of choice.</em></>}
            lead="Three capabilities that separate Fundle from every other daily sales reporting vendor in the market."
        >
            <div className="fp-grid-3" style={{ marginTop: 32 }}>
                {[
                    {
                        num: '01', t: 'Largest Brand Connectivity Layer', i: Network,
                        d: 'API. Agent. Utility. SFTP. CSV. ERP. POS. Restaurant systems. Cloud APIs. Legacy systems. Custom middleware. Future APIs. The platform adapts to the brand — never the other way around.',
                        chips: ['API', 'Agent', 'Utility', 'SFTP', 'CSV', 'Custom'],
                    },
                    {
                        num: '02', t: 'AI-Powered Retail Intelligence', i: Brain,
                        d: 'Executive insights, forecasts, recommendations, trend detection, category intelligence, tenant benchmarking, growth opportunities, sales anomalies, revenue leakage detection, peak-hour intelligence.',
                        chips: ['Forecasting', 'Anomalies', 'Benchmarking', 'Mall GPT', 'Segments'],
                    },
                    {
                        num: '03', t: 'Enterprise Data Accuracy Engine', i: ShieldCheck,
                        d: 'Daily reconciliation. Missing-sale alerts. Duplicate detection. Exception workflows. Monthly audit reports. Per-store confidence scoring. Complete audit trail with version history.',
                        chips: ['Reconciliation', 'Exceptions', 'Audit', 'Confidence', 'Versioning'],
                    },
                ].map((c, i) => (
                    <Reveal key={i} delay={i * 0.12}>
                        <div className="fp-feature-card" data-testid={`differentiator-${i}`}>
                            <div className="fp-feature-num">{c.num}</div>
                            <div className="fp-feature-icon"><c.i size={22} strokeWidth={1.6} /></div>
                            <h3 className="fp-h3">{c.t}</h3>
                            <p className="fp-p" style={{ fontSize: 14 }}>{c.d}</p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 16 }}>
                                {c.chips.map((chip) => (<span key={chip} className="fp-chip blue">{chip}</span>))}
                            </div>
                        </div>
                    </Reveal>
                ))}
            </div>
        </Section>
    );
}

/* ================================================================== */
/* 06 CONNECTIVITY                                                      */
/* ================================================================== */
function Connectivity() {
    const systems = [
        { n: 'Retail Pro', t: 'POS' }, { n: 'SAP', t: 'ERP' }, { n: 'Oracle', t: 'ERP' },
        { n: 'MS Dynamics', t: 'ERP' }, { n: 'LS Retail', t: 'POS' }, { n: 'Shopify POS', t: 'CLOUD' },
        { n: 'Ginesys', t: 'POS' }, { n: 'GoFrugal', t: 'POS' }, { n: 'Unicommerce', t: 'OMS' },
        { n: 'Logic ERP', t: 'ERP' }, { n: 'Tally', t: 'ACCT' }, { n: 'Petpooja', t: 'F&B' },
        { n: 'POSist', t: 'F&B' }, { n: 'Restaurant POS', t: 'F&B' }, { n: 'Custom APIs', t: 'API' },
        { n: 'CSV Feeds', t: 'FILE' }, { n: 'SFTP Drop', t: 'FILE' }, { n: 'Utility Sync', t: 'AGENT' },
        { n: 'Local Agent', t: 'AGENT' }, { n: 'Cloud APIs', t: 'API' }, { n: 'Legacy DB', t: 'DIRECT' },
        { n: 'Middleware', t: 'BRIDGE' }, { n: 'Excel Import', t: 'FILE' }, { n: 'Future APIs', t: 'READY' },
    ];
    const brands = [
        'Reliance Retail', 'Aditya Birla Fashion', 'Lifestyle', 'Shoppers Stop', 'Central',
        'H&M', 'Zara', 'Marks & Spencer', 'Tommy Hilfiger', 'Calvin Klein', 'Nike', 'Adidas',
        'Puma', "Levi's", 'GAP', 'UNIQLO', 'Uniqlo', 'Superdry', 'Guess', 'GANT',
        'Sephora', 'Nykaa', 'MAC', 'The Body Shop', 'Bath & Body Works',
        'Starbucks', "McDonald's", 'Burger King', 'KFC', 'Pizza Hut', 'Chai Point',
        'Tanishq', 'CaratLane', 'Kalyan', 'Malabar Gold',
        'Croma', 'Reliance Digital', 'Apple', 'Samsung', 'Hamleys', 'Miniso',
    ];

    return (
        <Section
            id="connectivity"
            eyebrow="06 · Brand Integration Layer"
            title={<>The platform <em>speaks every language</em> retail speaks.</>}
            lead="Fundle supports every meaningful pattern in Indian and global retail — from Fortune 500 ERPs to Tally-based standalone stores to modern cloud POS to legacy custom middleware."
        >
            <Reveal delay={0.1}>
                <h4 className="fp-h4" style={{ marginTop: 32 }}>Supported Systems</h4>
                <div className="fp-matrix" style={{ marginTop: 12 }} data-testid="connectivity-matrix">
                    {systems.map((s, i) => (
                        <div className="fp-matrix-cell" key={i}>
                            <div>{s.n}</div>
                            <span className="type">{s.t}</span>
                        </div>
                    ))}
                </div>
            </Reveal>

            <Reveal delay={0.2}>
                <div style={{ marginTop: 48 }}>
                    <h4 className="fp-h4">Deployment-Ready Across Leading Brands</h4>
                    <div className="fp-marquee-row" style={{ marginTop: 12 }}>
                        <div className="track">
                            {[...brands, ...brands].map((b, i) => (
                                <span className="brand" key={i}>{b}</span>
                            ))}
                        </div>
                    </div>
                </div>
            </Reveal>

            <Reveal delay={0.35}>
                <div style={{ marginTop: 48, padding: 40, background: 'var(--fp-navy)', color: 'white', borderRadius: 'var(--fp-radius-lg)', position: 'relative', overflow: 'hidden' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40, alignItems: 'center' }}>
                        <div>
                            <div className="fp-eyebrow" style={{ color: 'var(--fp-accent)' }}>
                                <span style={{ background: 'var(--fp-accent)' }} />
                                Integration Readiness
                            </div>
                            <div className="fp-kpi-num" style={{ color: 'white', fontSize: 88 }}>
                                <Counter to={95} suffix="%+" />
                            </div>
                            <p style={{ color: 'rgba(255,255,255,0.75)', marginTop: 12, fontSize: 15, maxWidth: 380 }}>
                                Fundle already covers 95%+ of the retail integration surface required for a mall the scale of Inorbit — with 100% coverage available via custom bridges within four weeks.
                            </p>
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {['API', 'Agent', 'Utility', 'SFTP', 'CSV', 'ERP', 'POS', 'Cloud', 'Legacy', 'F&B', 'Custom', 'Future'].map((c) => (
                                <span key={c} style={{ padding: '10px 16px', border: '1px solid rgba(255,255,255,0.25)', color: 'white', borderRadius: 100, fontSize: 13, fontFamily: 'JetBrains Mono, monospace' }}>{c}</span>
                            ))}
                        </div>
                    </div>
                </div>
            </Reveal>
        </Section>
    );
}

/* ================================================================== */
/* 07 ARCHITECTURE                                                      */
/* ================================================================== */
function Architecture() {
    const stages = [
        { i: 'Sources',      n: 'Brand POS · ERP · Cloud · Restaurant Systems · Legacy',        t: 'INGESTION EDGE', icon: Store },
        { i: 'Collection',   n: 'API · Agent · Utility · SFTP · CSV — parallel, resilient',     t: 'COLLECTOR LAYER', icon: Radio },
        { i: 'Validation',   n: 'Schema, tax rules, negative sales, store-hour checks',         t: 'RULES ENGINE', icon: FileCheck2 },
        { i: 'Normalisation', n: 'Currency, tax, category, brand-ID, mall-ID canonicalisation', t: 'CANONICAL SCHEMA', icon: Workflow },
        { i: 'Reconciliation', n: 'Daily match: expected vs reported vs footfall / bills',      t: 'MATCH ENGINE', icon: Gauge },
        { i: 'Warehouse',    n: 'Columnar analytics store, partitioned by mall & date',         t: 'DATA WAREHOUSE', icon: Database },
        { i: 'AI Engine',    n: 'Forecasting · Anomaly · Benchmarking · Mall GPT · Segments',   t: 'INTELLIGENCE LAYER', icon: Brain },
        { i: 'Surfaces',     n: 'Executive dashboards · Mobile app · Alerts · Mall GPT',        t: 'DELIVERY', icon: BarChart3 },
        { i: 'Decisions',    n: 'Leasing · Staffing · Marketing · Tenant management',           t: 'BUSINESS OUTCOMES', icon: Target },
    ];
    return (
        <Section
            id="architecture"
            eyebrow="07 · Data Collection Architecture"
            title={<>Nine stages. <em>End to end.</em></>}
            lead="Every data point in the platform traverses the same nine stages — no exceptions. That is what guarantees accuracy at scale and makes every number auditable."
        >
            <div className="fp-arch-stack" style={{ marginTop: 32 }} data-testid="architecture-stack">
                {stages.map((s, i) => (
                    <React.Fragment key={i}>
                        <Reveal delay={i * 0.06}>
                            <div className="fp-arch-node">
                                <div className="fp-arch-idx">{String(i + 1).padStart(2, '0')}</div>
                                <div>
                                    <div className="fp-arch-name">{s.i}</div>
                                    <div className="fp-arch-desc">{s.n}</div>
                                </div>
                                <span className="fp-arch-tag">{s.t}</span>
                            </div>
                        </Reveal>
                        {i < stages.length - 1 && <div className="fp-arch-connector" />}
                    </React.Fragment>
                ))}
            </div>
        </Section>
    );
}

/* ================================================================== */
/* 08 SECURITY                                                          */
/* ================================================================== */
function Security() {
    const items = [
        { i: Lock, t: 'Encryption in Transit', d: 'TLS 1.3 on every connection · certificate pinning on agents' },
        { i: ShieldCheck, t: 'Encryption at Rest', d: 'AES-256 database encryption · KMS-managed keys' },
        { i: Fingerprint, t: 'API Authentication', d: 'OAuth 2.0 · signed JWT · rotating tokens per brand' },
        { i: Users, t: 'Role-Based Access', d: 'Fine-grained roles: viewer / operator / manager / admin' },
        { i: FileCheck2, t: 'Audit Logs', d: 'Every read, write and reconciliation event captured' },
        { i: Server, t: 'Redundant Infrastructure', d: 'Multi-AZ deployment · warm standby · automated failover' },
        { i: CloudLightning, t: 'Disaster Recovery', d: 'Encrypted daily backups · point-in-time recovery' },
        { i: Radar, t: 'Continuous Monitoring', d: '24/7 SIEM feed · anomaly alerts · penetration tested' },
        { i: GraduationCap, t: 'ISO 27001 Ready', d: 'Controls mapped · certification path underway' },
    ];
    return (
        <Section
            id="security"
            eyebrow="08 · Security Architecture"
            title={<>Enterprise-grade security. <em>Built in — not bolted on.</em></>}
            lead="Sales data is one of a mall's most sensitive assets. Fundle is architected to enterprise banking-grade security standards from the ground up."
        >
            <div className="fp-grid-3" style={{ marginTop: 32 }}>
                {items.map((it, i) => (
                    <Reveal key={i} delay={i * 0.05}>
                        <div className="fp-card" data-testid={`security-${i}`}>
                            <div className="fp-feature-icon" style={{ width: 40, height: 40, marginBottom: 12 }}>
                                <it.i size={20} strokeWidth={1.8} />
                            </div>
                            <h3 className="fp-h3" style={{ fontSize: 16 }}>{it.t}</h3>
                            <p className="fp-p" style={{ fontSize: 13, marginBottom: 0 }}>{it.d}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </Section>
    );
}

/* ================================================================== */
/* 09 DATA ACCURACY                                                     */
/* ================================================================== */
function Accuracy() {
    const controls = [
        'Daily reconciliation across every store & every bill',
        'Zero-sales detection with automatic alerting',
        'Store-not-reporting alerts within one hour of expected sync',
        'Duplicate invoice / bill number detection',
        'Negative sales identification (returns misclassified as sales)',
        'Statistical outlier flagging (day-over-day, week-over-week)',
        'Store closure validation against declared operational hours',
        'Missing bill number detection (sequence gap analysis)',
        'Monthly audit-grade reconciliation reports',
        'Exception workflow: raise → investigate → approve → resolve',
        'Store & mall confidence scores (0-100) per day',
        'Complete audit trail with version history & user attribution',
    ];
    return (
        <Section
            id="accuracy"
            eyebrow="09 · Data Accuracy & Audit"
            title={<>Numbers you can <em>defend in the boardroom.</em></>}
            lead="Fundle treats data accuracy as a first-class product. Every reported sale traverses twelve accuracy controls before entering the warehouse."
        >
            <div className="fp-grid-2" style={{ marginTop: 32, alignItems: 'start' }}>
                <div>
                    <ul className="fp-bullets check" data-testid="accuracy-list">
                        {controls.map((c, i) => (
                            <li key={i}>{c}</li>
                        ))}
                    </ul>
                </div>
                <div>
                    <div className="fp-card fp-card-dark" style={{ padding: 32 }}>
                        <h4 className="fp-h4" style={{ color: 'var(--fp-accent)' }}>CONFIDENCE INDEX</h4>
                        <div style={{ marginTop: 20 }}>
                            {[
                                { l: 'Mall A · Malad', v: 98.6, c: 'var(--fp-accent)' },
                                { l: 'Mall B · Vashi', v: 96.2, c: 'var(--fp-accent)' },
                                { l: 'Mall C · Cyber', v: 91.8, c: 'var(--fp-accent)' },
                                { l: 'Mall D · Vadodara', v: 84.1, c: '#F5C400' },
                                { l: 'Mall E · Hubballi', v: 78.4, c: 'var(--fp-warn)' },
                            ].map((m, i) => (
                                <div key={i} style={{ marginBottom: 14 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'rgba(255,255,255,0.85)', marginBottom: 6 }}>
                                        <span>{m.l}</span>
                                        <span style={{ fontFamily: 'JetBrains Mono, monospace', color: m.c }}>{m.v.toFixed(1)}%</span>
                                    </div>
                                    <div style={{ height: 6, background: 'rgba(255,255,255,0.1)', borderRadius: 3 }}>
                                        <motion.div
                                            initial={{ width: 0 }}
                                            whileInView={{ width: `${m.v}%` }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.1 + i * 0.1 }}
                                            style={{ height: '100%', borderRadius: 3, background: m.c }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </Section>
    );
}

/* ================================================================== */
/* 10 ANALYTICS PLATFORM                                                */
/* ================================================================== */
function Analytics() {
    const salesData = [
        { d: 'Mon', sales: 12.4, bills: 8.1 },
        { d: 'Tue', sales: 13.8, bills: 8.7 },
        { d: 'Wed', sales: 14.2, bills: 9.0 },
        { d: 'Thu', sales: 15.9, bills: 10.2 },
        { d: 'Fri', sales: 18.4, bills: 11.8 },
        { d: 'Sat', sales: 22.9, bills: 14.5 },
        { d: 'Sun', sales: 24.7, bills: 15.7 },
    ];
    const hourly = Array.from({ length: 12 }).map((_, i) => ({
        h: `${11 + i}:00`,
        v: [8, 12, 22, 34, 28, 18, 24, 42, 58, 68, 54, 32][i],
    }));
    const mix = [
        { name: 'Apparel', value: 34, c: '#1E4FDB' },
        { name: 'Beauty', value: 18, c: '#00D4A0' },
        { name: 'F&B', value: 22, c: '#0A1B3B' },
        { name: 'Electronics', value: 14, c: '#FF7A45' },
        { name: 'Other', value: 12, c: '#8A94A6' },
    ];
    return (
        <Section
            id="analytics"
            eyebrow="10 · Executive Analytics Platform"
            title={<>Not dashboards. <em>Decision surfaces.</em></>}
            lead="Every screen is designed around a decision, not a metric. Executives arrive with a question — the platform leaves with a signed action."
        >
            <div style={{ marginTop: 32, display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 20 }}>
                {/* Sales/Bills */}
                <div className="fp-dash">
                    <div className="fp-dash-grid" />
                    <div style={{ position: 'relative' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                            <div>
                                <h4>Weekly GMV · All Malls</h4>
                                <div className="fp-dash-value">₹<Counter to={122.3} decimals={1} /><span className="unit"> Cr</span></div>
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8, padding: '4px 10px', background: 'rgba(0, 212, 160, 0.15)', color: 'var(--fp-accent)', borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                                    <TrendingUp size={12} /> +14.2% WoW
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <span className="fp-chip" style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.8)', borderColor: 'rgba(255,255,255,0.15)' }}>Sales (₹Cr)</span>
                                <span className="fp-chip" style={{ background: 'rgba(0, 212, 160, 0.15)', color: 'var(--fp-accent)', borderColor: 'rgba(0, 212, 160, 0.3)' }}>Bills (K)</span>
                            </div>
                        </div>
                        <ResponsiveContainer width="100%" height={220}>
                            <AreaChart data={salesData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="gSales" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#4F7FFF" stopOpacity={0.5} />
                                        <stop offset="100%" stopColor="#4F7FFF" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <XAxis dataKey="d" stroke="rgba(255,255,255,0.5)" fontSize={11} axisLine={false} tickLine={false} />
                                <YAxis stroke="rgba(255,255,255,0.5)" fontSize={11} axisLine={false} tickLine={false} />
                                <Tooltip contentStyle={{ background: '#0A1B3B', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, fontSize: 12 }} labelStyle={{ color: 'white' }} />
                                <Area type="monotone" dataKey="sales" stroke="#4F7FFF" strokeWidth={2.5} fill="url(#gSales)" />
                                <Line type="monotone" dataKey="bills" stroke="#00D4A0" strokeWidth={2} dot={false} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Category mix */}
                <div className="fp-card" style={{ padding: 24 }}>
                    <h4 className="fp-h4">Category Mix</h4>
                    <ResponsiveContainer width="100%" height={220}>
                        <PieChart>
                            <Pie data={mix} innerRadius={60} outerRadius={90} dataKey="value" paddingAngle={4}>
                                {mix.map((e, i) => (<Cell key={i} fill={e.c} />))}
                            </Pie>
                            <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                        </PieChart>
                    </ResponsiveContainer>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 8 }}>
                        {mix.map((e) => (
                            <div key={e.name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                                <span style={{ width: 8, height: 8, borderRadius: 2, background: e.c }} />
                                <span style={{ color: 'var(--fp-ink-soft)' }}>{e.name}</span>
                                <span style={{ marginLeft: 'auto', fontFamily: 'JetBrains Mono, monospace', color: 'var(--fp-navy)' }}>{e.value}%</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Second row */}
            <div style={{ marginTop: 20, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20 }}>
                <div className="fp-card" style={{ padding: 20 }}>
                    <h4 className="fp-h4">Hourly Sales · Today</h4>
                    <ResponsiveContainer width="100%" height={140}>
                        <BarChart data={hourly} margin={{ top: 5, right: 0, left: -25, bottom: 0 }}>
                            <XAxis dataKey="h" stroke="var(--fp-mute)" fontSize={9} axisLine={false} tickLine={false} interval={1} />
                            <YAxis stroke="var(--fp-mute)" fontSize={9} axisLine={false} tickLine={false} />
                            <Tooltip contentStyle={{ borderRadius: 8, fontSize: 11 }} />
                            <Bar dataKey="v" fill="#1E4FDB" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                    <div style={{ fontSize: 11, color: 'var(--fp-mute)' }}>Peak: 8 PM · 68% of avg daily bills</div>
                </div>

                <div className="fp-card" style={{ padding: 20 }}>
                    <h4 className="fp-h4">Top Brands Today</h4>
                    <div style={{ marginTop: 8 }}>
                        {[
                            { b: 'Zara', v: 18.4, c: '#0A1B3B' },
                            { b: 'H&M', v: 14.2, c: '#1E4FDB' },
                            { b: 'Sephora', v: 12.8, c: '#00D4A0' },
                            { b: 'Adidas', v: 10.5, c: '#4F7FFF' },
                            { b: 'Nykaa', v: 8.9, c: '#8A94A6' },
                        ].map((r, i) => (
                            <div key={i} style={{ marginBottom: 10 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                                    <span>{r.b}</span>
                                    <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>₹{r.v} L</span>
                                </div>
                                <div style={{ height: 4, background: 'var(--fp-canvas)', borderRadius: 2 }}>
                                    <motion.div
                                        initial={{ width: 0 }}
                                        whileInView={{ width: `${(r.v / 20) * 100}%` }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 1, delay: 0.05 * i, ease: [0.16, 1, 0.3, 1] }}
                                        style={{ height: '100%', borderRadius: 2, background: r.c }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="fp-card" style={{ padding: 20 }}>
                    <h4 className="fp-h4">Occupancy & Sales Density</h4>
                    <ResponsiveContainer width="100%" height={140}>
                        <RadialBarChart cx="50%" cy="50%" innerRadius="40%" outerRadius="90%" data={[{ n: 'Occ', v: 92, fill: '#1E4FDB' }, { n: 'Sales/sqft', v: 76, fill: '#00D4A0' }, { n: 'Traffic', v: 84, fill: '#0A1B3B' }]}>
                            <RadialBar dataKey="v" cornerRadius={6} background={{ fill: 'var(--fp-canvas)' }} />
                        </RadialBarChart>
                    </ResponsiveContainer>
                    <div style={{ display: 'flex', gap: 12, fontSize: 11, justifyContent: 'space-around' }}>
                        <span><span style={{ color: '#1E4FDB' }}>■</span> Occupancy 92%</span>
                        <span><span style={{ color: '#00D4A0' }}>■</span> Density 76%</span>
                    </div>
                </div>
            </div>

            <div style={{ marginTop: 32, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {['Mall Performance', 'Brand Performance', 'Category Performance', 'Sales Heatmaps',
                  'Hourly Sales', 'Hourly Bills', 'ATV', 'Conversion', 'Payment Mix', 'Growth YoY',
                  'MoM · WoW', 'Store Ranking', 'Tenant Ranking', 'Leasing Insights',
                  'Occupancy', 'Sales Density', 'Revenue Per Sq Ft'].map((c) => (
                    <span key={c} className="fp-chip">{c}</span>
                ))}
            </div>
        </Section>
    );
}

/* ================================================================== */
/* 11 MANAGEMENT QUESTIONS                                              */
/* ================================================================== */
function Questions() {
    const qs = [
        'Which tenants deserve expansion into a bigger store?',
        'Which stores are quietly underperforming this quarter?',
        "Who hasn't reported today — and is it a sync issue or a real problem?",
        'Which category is slowing down and by how much?',
        'What will month-end revenue look like from today\'s pace?',
        'Which brand is showing the highest growth momentum right now?',
        'Which tenants are at rental risk before their next renewal?',
        'Where is revenue leaking — void bills, cancelled invoices, unreconciled returns?',
        'What should we expect this weekend given weather + IPL + Diwali overlap?',
        'What is the forecast for the upcoming festival?',
        'Which hour of the day drives the highest ATV — and why?',
        'Which floor is quietly the most productive on weekends?',
        'Where are the biggest category opportunities we\'re missing?',
    ];
    return (
        <Section
            id="questions"
            eyebrow="11 · The Questions That Actually Matter"
            title={<>Fundle is built for <em>the questions executives ask.</em></>}
            lead="A great retail intelligence platform is measured by the questions it can answer with one click — not the reports it can generate every morning."
        >
            <div className="fp-grid-2" style={{ marginTop: 32, gap: 16 }}>
                {qs.map((q, i) => (
                    <Reveal key={i} delay={i * 0.04}>
                        <div className="fp-q-card" data-testid={`question-${i}`}>{q}</div>
                    </Reveal>
                ))}
            </div>
        </Section>
    );
}

/* ================================================================== */
/* 12 MALL GPT                                                          */
/* ================================================================== */
function MallGPT() {
    const convos = [
        { u: 'Show me all brands trending below their target this month.', a: '17 brands below target across 6 malls. Top 3 by gap: Guess (-24%), GAP (-19%), Steve Madden (-17%). Weakest mall: Vadodara (5 brands below). Suggested action: rental discussion + Q4 campaign inclusion.' },
        { u: 'Predict August revenue for Inorbit Malad.', a: 'Forecast: ₹41.8 Cr ± 4.2%. Includes Independence Day uplift (+8.4%), monsoon drag (-3.1%), and Ganesh Chaturthi tail (+11.6%). Confidence: 88%.' },
        { u: 'When it rains, which categories drop the hardest?', a: 'F&B footfall drops 22% on heavy rain days. Apparel drops 14%. Beauty is rain-resilient (-2%). Cinema and Games rise +9%. Recommendation: dynamic rain-day offers on F&B.' },
        { u: 'List stores with unusual returns this week.', a: '4 stores flagged: 2 with return rate >12% (industry avg 4%), 2 with returns preceding weekend restock. All flagged for RCA. Draft email prepared.' },
        { u: 'Suggest three leasing opportunities.', a: 'Ground floor F&B expansion (Malad, 1,200 sqft vacant), 2F Beauty consolidation (Vashi, 3 brands overlap), Weekend-only pop-up strip (Cyberabad, 400 sqft prime).' },
    ];
    return (
        <Section
            id="mallgpt"
            eyebrow="12 · AI Intelligence Layer · Mall GPT"
            title={<>Ask the mall <em>anything.</em></>}
            lead="Mall GPT is a natural-language interface on top of the entire Fundle warehouse. Executives type a question — the platform returns numbers, charts, benchmarks and next actions."
        >
            <div className="fp-grid-2" style={{ marginTop: 32 }}>
                {convos.map((c, i) => (
                    <Reveal key={i} delay={i * 0.08}>
                        <div className="fp-prompt" data-testid={`mallgpt-${i}`}>
                            <div className="fp-prompt-user">{c.u}</div>
                            <div className="fp-prompt-ai">{c.a}</div>
                        </div>
                    </Reveal>
                ))}
            </div>

            <Reveal delay={0.4}>
                <div style={{ marginTop: 40, padding: 32, background: 'var(--fp-blue-soft)', borderRadius: 'var(--fp-radius-lg)' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                        {[
                            { k: 'Insight Generation', v: 'Automatic', d: 'Insights are pushed, not pulled' },
                            { k: 'Response Time', v: '<3s', d: 'Warehouse queries + LLM synthesis' },
                            { k: 'Grounded on', v: 'Your Data', d: 'Zero hallucination · full citations' },
                        ].map((k, i) => (
                            <div key={i}>
                                <div className="fp-kpi-label">{k.k}</div>
                                <div className="fp-kpi-num" style={{ fontSize: 32, marginTop: 4 }}>{k.v}</div>
                                <div className="fp-kpi-sub">{k.d}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </Reveal>
        </Section>
    );
}

/* ================================================================== */
/* 13 FORECASTING                                                       */
/* ================================================================== */
function Forecasting() {
    const drivers = [
        { i: '🗓', t: 'Daily · Weekly · Monthly', d: 'Base rolling forecasts, updated every night' },
        { i: '🎉', t: 'Festival Windows', d: 'Diwali, Eid, Christmas, Onam, Pongal, Ganesh' },
        { i: '🌦', t: 'Weather Signals', d: 'Rain / heatwave models tied to category impact' },
        { i: '💼', t: 'Salary Week Effect', d: 'Month-end + 1st week bump quantified per brand' },
        { i: '🏏', t: 'IPL & Cricket Fixtures', d: 'Match-day dip in F&B, rise in Home Delivery' },
        { i: '🎒', t: 'School Holidays', d: 'Kids brands, F&B, Entertainment uplift patterns' },
        { i: '☔', t: 'Monsoon Curves', d: 'Weekly footfall drag across weeks 24–34' },
        { i: '🎬', t: 'Movie Releases', d: 'PVR-INOX schedule tied to weekend visit forecast' },
        { i: '✈️', t: 'Tourism & Events', d: 'Trade fairs, weddings, city events layered in' },
        { i: '📈', t: 'Historical Trends', d: 'Multi-year seasonality regressed per brand-mall' },
    ];
    const forecastData = Array.from({ length: 30 }, (_, i) => ({
        d: i + 1,
        actual: i < 20 ? 12 + Math.sin(i / 3) * 3 + Math.random() * 2 : null,
        forecast: 12 + Math.sin(i / 3) * 3.2 + (i >= 18 ? (i - 18) * 0.4 : 0),
    }));
    return (
        <Section
            id="forecasting"
            eyebrow="13 · Forecasting Engine"
            title={<>Predict the mall <em>the way weather is predicted.</em></>}
            lead="Fundle's forecasting engine layers ten independent signal families to produce daily, weekly and event-based projections — with confidence bands."
        >
            <div style={{ marginTop: 32, display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 24 }}>
                <div className="fp-card" style={{ padding: 24 }}>
                    <h4 className="fp-h4">30-Day Rolling Forecast · GMV</h4>
                    <ResponsiveContainer width="100%" height={260}>
                        <LineChart data={forecastData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                            <defs>
                                <linearGradient id="gFore" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#1E4FDB" stopOpacity={0.4} />
                                    <stop offset="100%" stopColor="#1E4FDB" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <XAxis dataKey="d" stroke="var(--fp-mute)" fontSize={10} axisLine={false} tickLine={false} />
                            <YAxis stroke="var(--fp-mute)" fontSize={10} axisLine={false} tickLine={false} />
                            <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                            <Line type="monotone" dataKey="actual" stroke="#0A1B3B" strokeWidth={2.4} dot={false} name="Actual" />
                            <Line type="monotone" dataKey="forecast" stroke="#1E4FDB" strokeWidth={2.4} strokeDasharray="6 4" dot={false} name="Forecast" />
                        </LineChart>
                    </ResponsiveContainer>
                    <div style={{ display: 'flex', gap: 20, fontSize: 12, marginTop: 4 }}>
                        <span><span style={{ color: '#0A1B3B' }}>━</span> Actual</span>
                        <span><span style={{ color: '#1E4FDB' }}>┅</span> Forecast</span>
                        <span style={{ marginLeft: 'auto', color: 'var(--fp-accent)' }}>Confidence: 89.4%</span>
                    </div>
                </div>

                <div>
                    <div className="fp-grid-2" style={{ gap: 8 }}>
                        {drivers.map((d, i) => (
                            <Reveal key={i} delay={i * 0.04}>
                                <div style={{ padding: 12, border: '1px solid var(--fp-hair)', borderRadius: 10 }}>
                                    <div style={{ fontSize: 20 }}>{d.i}</div>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--fp-navy)', marginTop: 4 }}>{d.t}</div>
                                    <div style={{ fontSize: 10.5, color: 'var(--fp-mute)', marginTop: 2, lineHeight: 1.4 }}>{d.d}</div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </div>
        </Section>
    );
}

/* ================================================================== */
/* 14 CUSTOMER INTELLIGENCE                                             */
/* ================================================================== */
function CustomerIntelligence() {
    const items = [
        { i: RefreshCw, t: 'Repeat Customers', d: 'Identify high-frequency visitors and cross-brand shoppers' },
        { i: Compass, t: 'Catchment Analysis', d: 'Real distances travelled per mall, per weekday vs weekend' },
        { i: Radar, t: 'Journey & Heatmaps', d: 'Zone-wise footfall, entry-exit flows, dwell time' },
        { i: ShoppingBag, t: 'Basket & LTV', d: 'What customers combine, how much they spend across a year' },
        { i: Target, t: 'High-Value Segments', d: 'Top 5% shoppers identified and mapped to campaigns' },
        { i: PackageSearch, t: 'Cross-Shopping', d: 'When Sephora shoppers also visit Nykaa, Zara, Starbucks' },
        { i: Sparkles, t: 'Campaign Attribution', d: 'Which offer drove which repeat visit, at what ROI' },
        { i: Users, t: 'Movement Segments', d: 'Family-day / date-night / solo-treat / kids-outing personas' },
    ];
    return (
        <Section
            id="customer"
            eyebrow="14 · Customer Intelligence"
            title={<>Know the shopper <em>the mall never asked.</em></>}
            lead="Fundle is future-ready for full customer intelligence — activated the day loyalty, WiFi captive-portal, or first-party checkout data is available."
        >
            <div className="fp-grid-4" style={{ marginTop: 32 }}>
                {items.map((it, i) => (
                    <Reveal key={i} delay={i * 0.05}>
                        <div className="fp-card" data-testid={`customer-${i}`}>
                            <it.i size={20} strokeWidth={1.6} style={{ color: 'var(--fp-blue)' }} />
                            <h3 className="fp-h3" style={{ fontSize: 15, marginTop: 12 }}>{it.t}</h3>
                            <p className="fp-p" style={{ fontSize: 12, marginBottom: 0 }}>{it.d}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </Section>
    );
}

/* ================================================================== */
/* 15 TENANT INTELLIGENCE                                               */
/* ================================================================== */
function TenantIntelligence() {
    const scoreItems = [
        { l: 'Growth Trajectory', v: 92 },
        { l: 'Billing Efficiency', v: 87 },
        { l: 'Average Bill Value', v: 78 },
        { l: 'Return Rate (inverse)', v: 94 },
        { l: 'Sales / sqft', v: 81 },
        { l: 'Occupancy Efficiency', v: 88 },
    ];
    return (
        <Section
            id="tenant"
            eyebrow="15 · Tenant Intelligence"
            title={<>A live health score <em>for every tenant.</em></>}
            lead="Fundle computes a composite Tenant Health Score every night — a single number leasing and operations can trust, backed by twelve underlying drivers."
        >
            <div style={{ marginTop: 32, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                <div className="fp-card" style={{ padding: 32, position: 'relative' }}>
                    <h4 className="fp-h4">Sample Tenant · Zara · Malad</h4>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginTop: 8 }}>
                        <div className="fp-kpi-num" style={{ fontSize: 80 }}>
                            <Counter to={87} />
                        </div>
                        <span style={{ color: 'var(--fp-mute)', fontSize: 14 }}>/ 100</span>
                        <span className="fp-chip blue" style={{ marginLeft: 'auto' }}>Tier: A · Expand</span>
                    </div>
                    <div style={{ marginTop: 20 }}>
                        {scoreItems.map((s, i) => (
                            <div key={i} style={{ marginBottom: 12 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                                    <span style={{ color: 'var(--fp-ink-soft)' }}>{s.l}</span>
                                    <span style={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--fp-navy)' }}>{s.v}</span>
                                </div>
                                <div style={{ height: 4, background: 'var(--fp-canvas)', borderRadius: 2 }}>
                                    <motion.div
                                        initial={{ width: 0 }}
                                        whileInView={{ width: `${s.v}%` }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 1, delay: 0.05 * i, ease: [0.16, 1, 0.3, 1] }}
                                        style={{ height: '100%', borderRadius: 2, background: s.v > 85 ? 'var(--fp-accent)' : s.v > 70 ? 'var(--fp-blue)' : 'var(--fp-warn)' }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div>
                    <h4 className="fp-h4">What the score powers</h4>
                    <ul className="fp-bullets check" style={{ marginTop: 8 }}>
                        <li>Lease renewal decisions with objective, defensible data</li>
                        <li>Expansion / downsize recommendations by brand and by mall</li>
                        <li>Rental restructuring conversations backed by growth vectors</li>
                        <li>Category-level opportunity mapping across the portfolio</li>
                        <li>Executive dashboards ranking every tenant, every week</li>
                        <li>Weekly outreach lists for underperforming stores</li>
                    </ul>
                </div>
            </div>
        </Section>
    );
}

/* ================================================================== */
/* 16 OPERATIONS INTELLIGENCE                                           */
/* ================================================================== */
function OperationsIntelligence() {
    const ops = [
        { i: '⏱', t: 'Peak Hour Intelligence', d: 'Staff, security and F&B stations aligned to real footfall' },
        { i: '🧹', t: 'Housekeeping Rhythm', d: 'Cleaning slots optimised to lulls, not fixed schedules' },
        { i: '🅿️', t: 'Parking Demand', d: 'Advance projections for valet, EV bays, tow zones' },
        { i: '🛡', t: 'Security Staffing', d: 'Guard deployment matched to hourly footfall & events' },
        { i: '🛗', t: 'Escalator Usage', d: 'Runtime, maintenance windows scheduled off-peak' },
        { i: '⚡', t: 'Energy Optimisation', d: 'Zone-wise HVAC + lighting driven by occupancy' },
        { i: '🍽', t: 'Food-Court Congestion', d: 'Live seating heatmaps + expected wait times' },
        { i: '🚨', t: 'Facility Alerts', d: 'Escalation flows for lift outages, wash-room issues, spills' },
    ];
    return (
        <Section
            id="operations"
            eyebrow="16 · Operations Intelligence"
            title={<>The mall as an <em>operational organism.</em></>}
            lead="When the data platform sees the whole mall, the operations team can start running it like a Formula-1 pit crew instead of a shopping centre."
        >
            <div className="fp-grid-4" style={{ marginTop: 32 }}>
                {ops.map((o, i) => (
                    <Reveal key={i} delay={i * 0.05}>
                        <div className="fp-card" style={{ padding: 20 }} data-testid={`ops-${i}`}>
                            <div style={{ fontSize: 24 }}>{o.i}</div>
                            <h3 className="fp-h3" style={{ fontSize: 15, marginTop: 8 }}>{o.t}</h3>
                            <p className="fp-p" style={{ fontSize: 12, marginBottom: 0 }}>{o.d}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </Section>
    );
}

/* ================================================================== */
/* 17 ROADMAP                                                           */
/* ================================================================== */
function Roadmap() {
    const years = [
        { y: 'Year 1', focus: 'Foundation', c: 'var(--fp-blue)', items: ['ADSR — Daily Sales Reporting', 'Executive Dashboards', 'Analytics Platform', 'Reconciliation Engine'] },
        { y: 'Year 2', focus: 'Intelligence', c: 'var(--fp-navy)', items: ['Forecasting Engine (10 signal families)', 'AI Intelligence Layer', 'Mall GPT (natural language)', 'Customer Intelligence · Segments'] },
        { y: 'Year 3', focus: 'Autonomy', c: 'var(--fp-accent)', items: ['Digital Twin of the Mall', 'Predictive Leasing', 'Retail Media Platform', 'Campaign Intelligence · Loyalty · AI Agents'] },
    ];
    return (
        <Section
            id="roadmap"
            eyebrow="17 · Roadmap"
            title={<>A three-year <em>compounding platform.</em></>}
            lead="Each year builds on the last. ADSR is the wedge. Intelligence is the moat. Autonomy is the outcome."
        >
            <div className="fp-grid-3" style={{ marginTop: 32 }}>
                {years.map((y, i) => (
                    <Reveal key={i} delay={i * 0.12}>
                        <div style={{
                            padding: 32, borderRadius: 'var(--fp-radius-lg)',
                            background: i === 2 ? 'linear-gradient(135deg, var(--fp-navy), #051530)' : (i === 1 ? 'var(--fp-navy)' : 'var(--fp-blue-soft)'),
                            color: i === 0 ? 'var(--fp-ink)' : 'white',
                            position: 'relative', overflow: 'hidden', minHeight: 320,
                        }} data-testid={`roadmap-${i}`}>
                            <div style={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', background: y.c, opacity: 0.14, filter: 'blur(20px)' }} />
                            <div style={{ position: 'relative' }}>
                                <div style={{ fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 600, opacity: 0.7 }}>{y.y}</div>
                                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 44, fontWeight: 500, letterSpacing: '-0.02em', marginTop: 8 }}>{y.focus}</div>
                                <div style={{ height: 3, width: 40, background: y.c, marginTop: 12, marginBottom: 24 }} />
                                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                    {y.items.map((it, j) => (
                                        <li key={j} style={{ padding: '10px 0', borderBottom: `1px solid ${i === 0 ? 'rgba(30, 79, 219, 0.15)' : 'rgba(255,255,255,0.1)'}`, fontSize: 14 }}>{it}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </Reveal>
                ))}
            </div>
        </Section>
    );
}

/* ================================================================== */
/* 18 IMPLEMENTATION                                                    */
/* ================================================================== */
function Implementation() {
    const weeks = [
        { w: 'Week 1', t: 'Discovery', d: 'Mall walkthrough, brand inventory, integration mapping, security review' },
        { w: 'Week 2', t: 'POS Mapping', d: 'Per-brand system identification, extract format finalisation, sample data pulled' },
        { w: 'Week 3', t: 'Integrations', d: 'Parallel connector build — API / Agent / Utility / SFTP / CSV per brand' },
        { w: 'Week 4', t: 'Validation', d: 'Sample-week reconciliation, exception workflow tuning, tax + returns rules' },
        { w: 'Week 5', t: 'Dashboards', d: 'Executive surfaces, mall dashboards, brand dashboards, mobile app' },
        { w: 'Week 6', t: 'Pilot', d: 'Live pilot with 1 mall + 20 brands · shadow-mode reconciliation' },
        { w: 'Week 7', t: 'Audit', d: 'Month-end reconciliation exercise, sign-off from finance & operations' },
        { w: 'Week 8', t: 'Go Live', d: 'Full portfolio production cutover · training + change management' },
        { w: 'Week 9+', t: 'Optimisation', d: 'AI layer activation · Mall GPT rollout · forecasting calibration', future: true },
    ];
    return (
        <Section
            id="implementation"
            eyebrow="18 · Implementation Plan"
            title={<>Eight weeks to go-live. <em>Nine to accelerate.</em></>}
            lead="Fundle has industrialised the deployment path — every phase is instrumented, gated and reversible."
        >
            <div className="fp-timeline" style={{ marginTop: 32 }} data-testid="impl-timeline">
                {weeks.map((w, i) => (
                    <Reveal key={i} delay={i * 0.06}>
                        <div className={`fp-tl-item ${w.future ? 'future' : ''}`}>
                            <div className="fp-tl-dot" />
                            <div className="fp-tl-week">{w.w}</div>
                            <div className="fp-tl-title">{w.t}</div>
                            <div className="fp-tl-desc">{w.d}</div>
                        </div>
                    </Reveal>
                ))}
            </div>
        </Section>
    );
}

/* ================================================================== */
/* 19 REFERENCE                                                         */
/* ================================================================== */
function ReferenceDeployments() {
    const refs = [
        { t: 'Reference Malls', d: 'Multi-mall enterprise groups across metro India · confidential', c: '4+' },
        { t: 'Brand Deployments', d: 'From Fortune 500 anchors to Tally-based multi-format retail', c: '250+' },
        { t: 'Bills Processed / Month', d: 'End-to-end reconciled, audit-grade, at platform scale', c: '18M+' },
        { t: 'Store-Days Reconciled', d: 'Continuous accuracy tracking across 3-year rolling window', c: '1.2M+' },
    ];
    return (
        <Section
            id="reference"
            eyebrow="19 · Reference Deployments"
            title={<>Battle-tested at <em>enterprise scale.</em></>}
            lead="Fundle powers some of India's largest retail environments. References available under NDA."
        >
            <div className="fp-grid-4" style={{ marginTop: 32 }}>
                {refs.map((r, i) => (
                    <Reveal key={i} delay={i * 0.08}>
                        <div className="fp-card" style={{ padding: 24 }} data-testid={`ref-${i}`}>
                            <div className="fp-kpi-num" style={{ fontSize: 44 }}>{r.c}</div>
                            <div className="fp-kpi-label" style={{ marginTop: 12 }}>{r.t}</div>
                            <div className="fp-kpi-sub">{r.d}</div>
                        </div>
                    </Reveal>
                ))}
            </div>

            <Reveal delay={0.4}>
                <div className="fp-card fp-card-dark" style={{ marginTop: 32, padding: 32 }}>
                    <h4 className="fp-h4" style={{ color: 'var(--fp-accent)' }}>WHAT LEADERSHIP TEAMS SAY</h4>
                    <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 26, lineHeight: 1.5, color: 'white', fontStyle: 'italic', marginTop: 16 }}>
                        “Before Fundle, our monthly review meetings were about reconciling numbers. Since Fundle, they are about deciding what to do next.”
                    </p>
                    <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, marginTop: 16 }}>— Head of Retail Operations, Multi-Mall Enterprise Group · testimonial placeholder</div>
                </div>
            </Reveal>
        </Section>
    );
}

/* ================================================================== */
/* 20 COMMERCIALS / WHY FUNDLE                                          */
/* ================================================================== */
function Commercials() {
    const wins = [
        'Largest Connectivity Layer in Indian retail',
        'AI Intelligence Layer + Mall GPT out of the box',
        'Enterprise-Grade Security posture',
        'Daily Reconciliation + Monthly Audit built-in',
        '10-signal Forecasting Engine',
        'Scalable across hundreds of malls on a single stack',
        'Future-Ready — retail media, customer intelligence, digital twin',
        'India-first · Global-ready',
    ];
    return (
        <Section
            id="commercials"
            eyebrow="20 · Why Fundle Wins"
            title={<>Eight reasons this is <em>the right platform.</em></>}
            lead="One platform. One source of truth. Everything Inorbit needs to move from mall operator to mall intelligence company."
        >
            <div className="fp-grid-4" style={{ marginTop: 32 }}>
                {wins.map((w, i) => (
                    <Reveal key={i} delay={i * 0.05}>
                        <div className="fp-card" data-testid={`win-${i}`} style={{ padding: 20 }}>
                            <div style={{
                                width: 32, height: 32, borderRadius: 8,
                                background: 'var(--fp-navy)', color: 'white',
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 12,
                            }}>{String(i + 1).padStart(2, '0')}</div>
                            <div style={{ marginTop: 12, fontSize: 14, fontWeight: 600, color: 'var(--fp-navy)', lineHeight: 1.35 }}>{w}</div>
                        </div>
                    </Reveal>
                ))}
            </div>
        </Section>
    );
}

/* ================================================================== */
/* 21 THE FUTURE                                                        */
/* ================================================================== */
function TheFuture() {
    return (
        <section id="future" className="fp-end" data-testid="section-future">
            <div className="fp-end-orb" />
            <Reveal>
                <div className="fp-eyebrow" style={{ color: 'var(--fp-accent)' }}>21 · The Future</div>
                <h2 className="fp-end-title">
                    Fundle.<br />Building the <em>operating system</em><br />for shopping malls.
                </h2>
            </Reveal>

            <Reveal delay={0.3}>
                <div className="fp-end-lines">
                    One platform.<br />
                    One source of truth.<br />
                    Infinite intelligence.
                </div>
            </Reveal>

            <Reveal delay={0.6}>
                <div style={{ marginTop: 80, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 24, borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: 40 }}>
                    <div>
                        <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 36, color: 'var(--fp-accent)', fontStyle: 'italic' }}>Let's build.</div>
                        <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, letterSpacing: '0.2em', textTransform: 'uppercase', marginTop: 12 }}>Prepared for Inorbit Malls · February 2026</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 10 }}>Proposal by</div>
                        <div style={{ display: 'inline-block', padding: '14px 22px', background: 'white', borderRadius: 14, boxShadow: '0 12px 40px -12px rgba(0, 212, 160, 0.4)' }}>
                            <img src="/brand/fundle-logo.png" alt="Fundle" style={{ height: 52, width: 'auto', display: 'block' }} />
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', marginTop: 12 }}>Retail Intelligence Platform</div>
                    </div>
                </div>
            </Reveal>
        </section>
    );
}
